import { z } from 'zod';

export const emailSchema = z.string().email('Invalid email address');

export const phoneSchema = z
  .string()
  .regex(/^[6-9]\d{9}$/, 'Invalid Indian phone number');

export const pincodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Invalid pincode');

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

export const uuidSchema = z
  .string()
  .uuid('Invalid UUID format');

export function validateEmail(email: string): boolean {
  return emailSchema.safeParse(email).success;
}

export function validatePhone(phone: string): boolean {
  return phoneSchema.safeParse(phone).success;
}

export function validatePincode(pincode: string): boolean {
  return pincodeSchema.safeParse(pincode).success;
}

export function validateUuid(uuid: string): boolean {
  return uuidSchema.safeParse(uuid).success;
}

/**
 * List of common disposable email domains
 * Used to prevent referral abuse and fraudulent signups
 */
const DISPOSABLE_EMAIL_DOMAINS = [
  // Common temporary email services
  'tempmail.com',
  'throwaway.email',
  'guerrillamail.com',
  'mailinator.com',
  '10minutemail.com',
  'yopmail.com',
  'temp-mail.org',
  'getnada.com',
  'maildrop.cc',
  'trashmail.com',
  'fakeinbox.com',
  'mintemail.com',
  'sharklasers.com',
  'guerrillamailblock.com',
  'pokemail.net',
  'spam4.me',
  'mailnesia.com',
  'emailondeck.com',
  'mytemp.email',
  'mohmal.com',
  'tempinbox.com',
  'dispostable.com',
  'throwawaymail.com',
  'tempmailaddress.com',
  'emailfake.com',
  'tempr.email',
  'fakemail.net',
  'throwawayemailaddress.com',
  '33mail.com',
  'getairmail.com',
  'spambox.us',
  'mailcatch.com',
  'mailsac.com',
  'crazymailing.com',
  'mailforspam.com',
  'mintemail.com',
  'anonymousemail.me',
  'incognitomail.com',
  'temp-mail.io',
];

/**
 * Checks if an email address is from a disposable/temporary email service
 * @param email - Email address to check
 * @returns true if the email is from a disposable domain
 */
export function isDisposableEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) {
    return false;
  }

  return DISPOSABLE_EMAIL_DOMAINS.includes(domain);
}
